import _ from "lodash";
import React, { useState, useCallback } from "react";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import UnitSelection from "./UnitSelection";
import I18nHOC from "UIComponents/I18nHOC";
import withLoader from "UIComponents/GqlWithLoader";
import { ChecklistDropdown, FilterLabelButton } from "@toddle-edu/ds-web";

import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  ELEMENT_TYPE_PYP_THEME,
  SUBJECT_TYPE_GENERIC,
  SUBJECT_TYPE_OTHERS,
  SUBJECT_TYPE_TOK,
  SUBJECT_TYPE_LD,
  SUBJECT_TYPE_PPS,
} from "Constants/stringConstants";
import { getLabelValueForDropdown } from "modules/Services";
import {
  getLabelValueForMultiFilterLabelButton,
  getMemoizedFunction,
} from "Utils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  getFilterOptionsMemoized,
  getUpdatedFiltersMemoized,
} from "./modules/utils";

import {
  getOrganizationBasicCoursesQuery,
  getOrganizationSubjectsBasicDetailsQuery,
  getPypPlannerThemeSetNodesQuery,
  getStaffListQuery,
} from "modules/CommonQuery";
import {
  getOrganizationBasicCoursesFromCache,
  getOrganizationSubjectsBasicDetailsFromCache,
  getPypPlannerThemeSetNodesFromCache,
} from "modules/CommonGraphqlHelpers";
import { getOrganizationGradesQuery } from "CommonGraphql/Organization/OrganizationQueries";
import { getOrganizationGradesFromCache } from "CommonGraphql/Organization/OrganizationGraphqlHelpers";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";

const FILTER_TYPES = [
  "course",
  "unitType",
  "grade",
  "subject",
  "theme",
  "creator",
];
const TRUNCATE_LABEL_STYLE = {
  maxWidth: "100px",
};

const FiltersComponent = props => {
  const {
    t,
    coursesDropdownOptionsArr,
    courses,
    coursesLabelValue,
    testId,
    updateSelectedCourses,
    unitTypeDropdownOptionsArr,
    unitType,
    unitTypeLabelValue,
    updateSelectedUnitTypes,
    gradeOptions,
    selectedGrades,
    setSelectedGrades,
    subjectOptions,
    selectedSubjects,
    setSelectedSubjects,
    themeOptions,
    selectedThemes,
    creatorOptions,
    setSelectedThemes,
    selectedCreator,
    setSelectedCreator,
    filtersToShow,
    curriculumProgramType,
    shouldTruncateFilterLabels,
  } = props;

  const filtersConfig = {
    course: {
      options: coursesDropdownOptionsArr,
      value: courses,
      onChange: updateSelectedCourses,
      showAllOption: true,
      label: t("common:class"),
      labelValue: coursesLabelValue,
    },
    unitType: {
      options: unitTypeDropdownOptionsArr,
      value: unitType,
      onChange: updateSelectedUnitTypes,
      showAllOption: true,
      label: t("common:unit_type_label"),
      labelValue: unitTypeLabelValue,
      curriculums: [
        CURRICULUM_TYPE_PYP,
        CURRICULUM_TYPE_MYP,
        CURRICULUM_TYPE_UBD,
      ],
    },
    grade: {
      options: gradeOptions,
      value: selectedGrades,
      onChange: setSelectedGrades,
      showAllOption: true,
      showCountOnly: true,
      label: t("common:grade"),
    },
    subject: {
      options: subjectOptions,
      value: selectedSubjects,
      onChange: setSelectedSubjects,
      showAllOption: true,
      showCountOnly: true,
      isSearchEnabled: true,
      label: t("common:subject"),
    },
    theme: {
      options: themeOptions,
      value: selectedThemes,
      onChange: setSelectedThemes,
      showAllOption: true,
      showCountOnly: true,
      label: t("common:theme"),
      curriculums: [CURRICULUM_TYPE_PYP],
    },
    creator: {
      options: creatorOptions,
      value: selectedCreator,
      onChange: setSelectedCreator,
      showAllOption: true,
      showCountOnly: true,
      label: t("common:created_by"),
    },
  };

  return (
    <React.Fragment>
      {_.map(filtersToShow, filterType => {
        const filterConfig = filtersConfig[filterType];
        if (!filterConfig) return null;

        const {
          options,
          value,
          onChange,
          showAllOption,
          showCountOnly,
          isSearchEnabled,
          label,
          labelValue: labelValueFromProps,
          curriculums,
        } = filterConfig;

        if (curriculums && !_.includes(curriculums, curriculumProgramType))
          return null;

        const title = showCountOnly
          ? getLabelValueForMultiFilterLabelButton({
              options,
              selectedOptions: value,
              allText: t("common:all"),
              noneText: t("common:none"),
              t,
            })
          : getLabelValueForDropdown({
              selectedItems: value,
              itemList: options,
              noneSelectedText: t("common:select"),
              t,
            });

        const labelValue = labelValueFromProps ?? title;

        return (
          <ChecklistDropdown
            dsVersion="2.0"
            size="medium"
            options={options}
            value={value}
            onChange={onChange}
            showAllOption={showAllOption}
            isSearchEnabled={isSearchEnabled}
            showSelectedOptionsOnTop={false}
            testId={`${testId}-unitFilters-${label.replace(/ /g, "_")}`}
          >
            <FilterLabelButton
              testId={`${testId}-unitFilters-${label.replace(/ /g, "_")}`}
              dsVersion="2.0"
              size="medium"
              labelStyle={
                shouldTruncateFilterLabels ? TRUNCATE_LABEL_STYLE : {}
              }
              variant="primary"
              type="plain"
              label={label}
              labelValue={labelValue}
            />
          </ChecklistDropdown>
        );
      })}
    </React.Fragment>
  );
};

const getInitialSelectedFilters = (defaultSelections = [], options) => {
  if (_.size(defaultSelections) > 0) return defaultSelections;
  return _.map(options, "key");
};

const UnitSelectionWithFilters = props => {
  const {
    organizationId,
    curriculumProgramType,
    showFilters,
    testId = "",
    filters,
    coursesList,
    gradesList,
    creatorList,
    subjectsList,
    themesList,
    includedFiltersMapping,
    defaultSelections,
  } = props;

  const {
    unitTypeOptions,
    courseOptions,
    gradeOptions,
    subjectOptions,
    themeOptions,
    creatorOptions,
  } = getFilterOptionsMemoized({
    curriculumProgramType,
    organizationId,
    coursesList,
    includedFiltersMapping,
    gradesList,
    creatorList,
    subjectsList,
    themesList,
  });

  const [searchText, setSearchText] = useState("");
  const [selectedUnitTypes, setSelectedUnitTypes] = useState(
    getInitialSelectedFilters(
      _.get(defaultSelections, "unitTypes", []),
      unitTypeOptions
    )
  );
  const [selectedCourses, setSelectedCourses] = useState(
    getInitialSelectedFilters(
      _.get(defaultSelections, "courses", []),
      courseOptions
    )
  );
  const [selectedGrades, setSelectedGrades] = useState(
    getInitialSelectedFilters(
      _.get(defaultSelections, "grades", []),
      gradeOptions
    )
  );
  const [selectedSubjects, setSelectedSubjects] = useState(
    getInitialSelectedFilters(
      _.get(defaultSelections, "subjects", []),
      subjectOptions
    )
  );
  const [selectedThemes, setSelectedThemes] = useState(
    getInitialSelectedFilters(
      _.get(defaultSelections, "themes", []),
      themeOptions
    )
  );

  const [selectedCreator, setSelectedCreator] = useState(
    getInitialSelectedFilters(
      _.get(defaultSelections, "creator", []),
      creatorOptions
    )
  );

  const handleUnitSearch = useCallback(searchText => {
    setSearchText(searchText);
  }, []);

  const updateSelectedUnitTypes = useCallback(selectedUnitTypes => {
    setSelectedUnitTypes(selectedUnitTypes);
  }, []);

  const filterComponentProps = {
    unitTypeDropdownOptionsArr: unitTypeOptions,
    unitType: selectedUnitTypes,
    updateSelectedUnitTypes,
    coursesDropdownOptionsArr: courseOptions,
    courses: selectedCourses,
    updateSelectedCourses: setSelectedCourses,
    gradeOptions,
    selectedGrades,
    setSelectedGrades,
    subjectOptions,
    selectedSubjects,
    setSelectedSubjects,
    themeOptions,
    selectedThemes,
    setSelectedThemes,
    creatorOptions,
    selectedCreator,
    setSelectedCreator,
    ...props,
  };

  const filtersComponent = showFilters ? (
    <FiltersComponent {...filterComponentProps} />
  ) : null;

  const isAllGradesSelected = _.size(selectedGrades) == _.size(gradeOptions);

  const isAllSubjectsSelected =
    _.size(selectedSubjects) == _.size(subjectOptions);

  const isAllThemesSelected = _.size(selectedThemes) == _.size(themeOptions);

  const isAllUnitTypesSelected =
    _.size(selectedUnitTypes) == _.size(unitTypeOptions);

  const isAllCreatorSelected =
    _.size(selectedCreator) == _.size(creatorOptions);

  const updatedFilters = getUpdatedFiltersMemoized({
    filters,
    searchText,
    selectedUnitTypes,
    isAllUnitTypesSelected,
  });

  return (
    <UnitSelection
      searchText={searchText}
      testId={testId}
      handleUnitSearch={handleUnitSearch}
      filtersComponent={filtersComponent}
      unitTypes={isAllUnitTypesSelected ? [] : selectedUnitTypes}
      unitType={isAllUnitTypesSelected ? [] : selectedUnitTypes}
      grades={!isAllGradesSelected ? selectedGrades : []}
      subjects={!isAllSubjectsSelected ? selectedSubjects : []}
      themes={!isAllThemesSelected ? selectedThemes : []}
      createdBy={!isAllCreatorSelected ? selectedCreator : []}
      // spread props at last so we don't override if any of the above
      // has been already passed from parent
      {...props}
      filters={updatedFilters}
    />
  );
};

const isFilterIncluded = ({ ownProps, type, curriculumProgramType }) => {
  const {
    filtersToShow,
    coursesDropdownOptionsArr,
    courses,
    unitTypeDropdownOptionsArr,
    unitType,
    gradesList,
    subjectsList,
    creatorList,
    themesList,
  } = ownProps;

  const isIncluded = _.includes(filtersToShow, type);

  if (isIncluded) {
    switch (type) {
      case "course":
        return !coursesDropdownOptionsArr || !courses;

      case "unitType":
        return (
          curriculumProgramType != CURRICULUM_TYPE_DP &&
          (!unitTypeDropdownOptionsArr || !unitType)
        );

      case "grade":
        return !gradesList;

      case "subject":
        return !subjectsList;

      case "creator":
        return !creatorList;

      case "theme":
        return curriculumProgramType == CURRICULUM_TYPE_PYP && !themesList;
    }
  }

  return isIncluded;
};

const getSortedCreatorList = getMemoizedFunction(({ creatorList }) =>
  _.sortBy(creatorList, "isArchived")
);

const mapStateToProps = (state, ownProps) => {
  const { portalType, showFilters } = ownProps;

  const userInfo = getUserInfo({ portalType });
  const organizationId = userInfo.org_id;

  const currentCurriculumProgram = _.get(
    state,
    "platform.currentCurriculumProgram",
    {}
  );

  const {
    id: curriculumProgramId = "",
    type: curriculumProgramType = "",
  } = currentCurriculumProgram;

  const includedFiltersMapping = showFilters
    ? _.reduce(
        FILTER_TYPES,
        (result, type) => {
          result[type] = isFilterIncluded({
            ownProps,
            type,
            curriculumProgramType,
          });
          return result;
        },
        {}
      )
    : {};

  const curriculumProgramIds = curriculumProgramId ? [curriculumProgramId] : [];
  const plannerElementThemeSetId = _.get(
    state,
    `platform.currentPlannerElementSets.${ELEMENT_TYPE_PYP_THEME}.id`
  );

  // CAS and EE in DP core & SLR and RP in CP core do not have unit planning enabled
  const subjectTypes =
    curriculumProgramType === CURRICULUM_TYPE_DP
      ? [
          SUBJECT_TYPE_GENERIC,
          SUBJECT_TYPE_OTHERS,
          SUBJECT_TYPE_TOK,
          SUBJECT_TYPE_LD,
          SUBJECT_TYPE_PPS,
        ]
      : [];

  const academicYearId = getSelectedAcademicYearId(state);

  return {
    organizationId,
    curriculumProgramIds,
    plannerElementThemeSetId,
    curriculumProgramType,
    includedFiltersMapping,
    subjectTypes,
    isData: true,
    isLoading: false,
    academicYearId,
  };
};

const UnitSelectionWithFiltersComposed = compose(
  connect(mapStateToProps),
  I18nHOC({ resource: ["common"] }),
  graphql(getStaffListQuery, {
    name: "getStaffList",
    options: ({ organizationId, curriculumProgramIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: { organizationId, archivalState: "ALL", curriculumProgramIds },
    }),
    skip: ({ includedFiltersMapping }) => !includedFiltersMapping["creator"],
    props({ getStaffList, ownProps: { isData, isLoading } }) {
      const data = _.get(getStaffList, "node.staff.edges");
      const creatorList = _.map(data, item => ({
        value: item.node.id,
        label: getFormattedUserName({ user: item.node }),
        isArchived: _.get(item, "node.isArchived"),
      }));

      const sortedCreatorList = getSortedCreatorList({ creatorList });

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          isLoading || _.includes([1, 2], getStaffList["networkStatus"]),
        creatorList: sortedCreatorList,
      };
    },
  }),
  graphql(getOrganizationBasicCoursesQuery, {
    name: "getOrganizationBasicCourses",
    skip: ({ includedFiltersMapping }) => !includedFiltersMapping["course"],
    options: ({ organizationId, curriculumProgramIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId,
        filters: {
          curriculumProgramIds,
        },
      },
    }),
    props({
      getOrganizationBasicCourses,
      ownProps: { organizationId, curriculumProgramIds, isData, isLoading },
    }) {
      const data = getOrganizationBasicCoursesFromCache({
        organizationId,
        filters: {
          curriculumProgramIds,
        },
      });

      const coursesList = _.get(data, "courses", {});

      const networkStatus = _.get(getOrganizationBasicCourses, "networkStatus");
      return {
        coursesList,
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getOrganizationGradesQuery, {
    name: "getOrganizationGrades",
    options: ({ organizationId, curriculumProgramIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: organizationId,
        filters: {
          curriculumProgramIds,
        },
      },
    }),
    skip: ({ includedFiltersMapping }) => !includedFiltersMapping["grade"],
    props({
      getOrganizationGrades,
      ownProps: { organizationId, curriculumProgramIds, isData, isLoading },
    }) {
      const queryData = getOrganizationGradesFromCache({
        id: organizationId,
        filters: {
          curriculumProgramIds,
        },
      });

      const gradesList = _.get(queryData, "grades", []);
      return {
        gradesList,
        isData: !_.isEmpty(queryData) && isData,
        isLoading:
          getOrganizationGrades["networkStatus"] == 1 ||
          getOrganizationGrades["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getOrganizationSubjectsBasicDetailsQuery, {
    name: "getOrganizationSubjectsBasicDetails",
    options: ({ organizationId, curriculumProgramIds, subjectTypes }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: organizationId,
        filters: { curriculumProgramIds, types: subjectTypes },
      },
    }),
    skip: ({ includedFiltersMapping }) => !includedFiltersMapping["subject"],
    props: ({
      getOrganizationSubjectsBasicDetails,
      ownProps: {
        isData,
        isLoading,
        organizationId,
        curriculumProgramIds,
        subjectTypes,
      },
    }) => {
      const data = getOrganizationSubjectsBasicDetailsFromCache({
        id: organizationId,
        filters: { curriculumProgramIds, types: subjectTypes },
      });

      const subjectsList = _.get(data, "subjects", []);

      return {
        subjectsList,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          getOrganizationSubjectsBasicDetails["networkStatus"] == 1 ||
          getOrganizationSubjectsBasicDetails["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getPypPlannerThemeSetNodesQuery, {
    name: "getPypPlannerThemeSetNodes",
    skip: ({ includedFiltersMapping }) => !includedFiltersMapping["theme"],
    options: ({ plannerElementThemeSetId, academicYearId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        themeSetId: plannerElementThemeSetId,
        filters: {
          academicYear: academicYearId,
        },
      },
    }),
    props({
      getPypPlannerThemeSetNodes,
      ownProps: { plannerElementThemeSetId, isData, isLoading, academicYearId },
    }) {
      const queryData = getPypPlannerThemeSetNodesFromCache({
        variables: {
          themeSetId: plannerElementThemeSetId,
          filters: {
            academicYear: academicYearId,
          },
        },
      });

      const themesLoading = _.includes(
        [1, 2],
        getPypPlannerThemeSetNodes.networkStatus
      );

      return {
        themesList: _.get(queryData, "nodes", []),
        isData: !_.isEmpty(queryData) && isData,
        isLoading: isLoading || themesLoading,
      };
    },
  }),
  withLoader
)(UnitSelectionWithFilters);

const UnitSelectionWrapper = props => {
  const { showFilters, isSearchEnabled } = props;
  if (showFilters || isSearchEnabled)
    return <UnitSelectionWithFiltersComposed {...props} />;

  return <UnitSelection {...props} />;
};

UnitSelectionWrapper.defaultProps = {
  testId: "appComponents",
  showFilters: false,
  isSearchEnabled: false,
  filtersToShow: [],
  shouldTruncateFilterLabels: false,
  defaultSelections: {},
};

export default UnitSelectionWrapper;
